import { Input } from '~/components/common/CurieInput'
import { Button } from '~/components/common/Button'
import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { sendPhoneOTP, verifyPhoneOTP, resendPhoneOTP } from '~/services/userService'
import { performLogout } from '~/services/bridge/pluginAuth'
import { BetterToast } from '~/utils/toast'
import { Separator } from '../common/Separator'
import { useUserContext } from '~/contexts/UserContext'
import { useNavigate, useSearchParams } from 'react-router-dom'

type VerificationStep = 'phone' | 'otp'

export const PhoneVerificationPage = () => {
  const { user, refetch } = useUserContext()
  const [step, setStep] = useState<VerificationStep>('phone')
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Countdown timer for resend OTP
  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000)
    }
    return () => clearTimeout(timer)
  }, [countdown])

  useEffect(() => {
    if (!user) return
    if (!user.phone_verified) return
    const returnUrl = searchParams.get('return_url')
    if (returnUrl) {
      const decodedReturnUrl = decodeURIComponent(returnUrl)
      navigate(decodedReturnUrl, { replace: true })
    } else {
      navigate('/', { replace: true })
    }
  }, [user, searchParams, navigate])

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    // Basic international phone number validation
    const phoneRegex = /^\+[1-9]\d{9,14}$/
    return phoneRegex.test(phoneNumber.replace(/\s/g, ''))
  }

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters except +
    const cleaned = phoneNumber.replace(/[^\d+]/g, '')
    // Ensure it starts with +
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`
  }

  const handlePhoneSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) return

    if (!phone.trim()) {
      return BetterToast.error('Please enter your phone number')
    }

    const formattedPhone = formatPhoneNumber(phone)
    if (!validatePhoneNumber(formattedPhone)) {
      return BetterToast.error(
        'Please enter a valid international phone number (e.g., +1234567890)'
      )
    }

    setPhone(formattedPhone)

    try {
      setIsLoading(true)

      const result = await sendPhoneOTP(formattedPhone, user.email)

      if (result.success) {
        setStep('otp')
        setCountdown(60) // 1 minute countdown for resend
        BetterToast.success('Verification code sent to your phone')
      } else {
        throw new Error(result.message || 'Failed to send verification code')
      }
    } catch (err) {
      const error = err as Error
      BetterToast.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOtpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!user) return

    if (!otpCode.trim() || otpCode.length !== 6) {
      return BetterToast.error('Please enter the 6-digit verification code')
    }

    try {
      setIsLoading(true)

      const result = await verifyPhoneOTP(phone, otpCode, user.email)

      if (result.success) {
        BetterToast.success('Phone number verified successfully!')
        await refetch()
      } else {
        throw new Error(result.message || 'Invalid verification code')
      }
    } catch (err) {
      const error = err as Error
      BetterToast.error(error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (!user) return
    if (countdown > 0) return

    try {
      setIsResendLoading(true)

      const result = await resendPhoneOTP(phone, user.email)

      if (result.success) {
        setCountdown(60)
        BetterToast.success('Verification code resent')
      } else {
        throw new Error(result.message || 'Failed to resend verification code')
      }
    } catch (err) {
      const error = err as Error
      BetterToast.error(error.message)
    } finally {
      setIsResendLoading(false)
    }
  }

  const handleBackToSignIn = () => {
    performLogout()
  }

  if (step === 'otp') {
    return (
      <div className='flex-1 flex flex-col justify-center items-center w-full h-full pb-4'>
        <div className='w-full max-w-[450px] p-5 pt-24'>
          <h1
            aria-label='Phone verification title'
            className='mb-[40px] text-center font-semibold text-2xl'
          >
            Verify your phone
          </h1>

          <form id='phoneVerificationOtpForm' className='space-y-6' onSubmit={handleOtpSubmit}>
            <div className='text-center text-gray-600 mb-6'>
              We&apos;ve sent a 6-digit code to {phone}
            </div>
            <div>
              <Input
                type='text'
                id='otpCode'
                placeholder='Enter 6-digit code'
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className='text-center text-xl tracking-widest'
                autoComplete='one-time-code'
              />
            </div>
            <Button
              variant='primary'
              id='verify-phone'
              type='submit'
              className='mx-0 mt-4 h-8 w-full'
              isDisabled={isLoading || !otpCode || otpCode.length !== 6}
            >
              {isLoading ? 'Verifying...' : 'Verify Phone'}
            </Button>
            <div className='text-center'>
              <button
                type='button'
                onClick={handleResendOtp}
                disabled={countdown > 0 || isResendLoading}
                className='text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed'
              >
                {isResendLoading
                  ? 'Resending...'
                  : countdown > 0
                    ? `Resend code in ${countdown}s`
                    : 'Resend verification code'}
              </button>
            </div>
            <div className='text-center'>
              <button
                type='button'
                onClick={() => setStep('phone')}
                className='text-gray-600 hover:text-gray-800'
              >
                Change phone number
              </button>
            </div>
            <Separator text='or' className='mt-4' />
            <div className='text-center'>
              <button
                type='button'
                onClick={handleBackToSignIn}
                className='text-gray-600 hover:text-gray-800'
              >
                Back to Sign in
              </button>
            </div>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className='flex-1 flex flex-col justify-center items-center w-full h-full pb-4'>
      <div className='w-full max-w-[450px] p-5 pt-24'>
        <h1
          aria-label='Phone verification title'
          className='mb-[40px] text-center font-semibold text-2xl'
        >
          Add your phone number
        </h1>

        <form id='phoneVerificationForm' className='space-y-6' onSubmit={handlePhoneSubmit}>
          <div className='text-center text-gray-600 mb-6'>
            We&apos;ll send you a verification code to confirm your phone number
          </div>
          <div>
            <Input
              type='text'
              id='phone'
              placeholder='+1234567890'
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className='text-base'
              autoComplete='tel'
            />
            <p className='text-xs text-gray-500 mt-1'>
              Please enter your phone number with country code (e.g., +1 for US)
            </p>
          </div>
          <Button
            variant='primary'
            id='send-phone-code'
            type='submit'
            className='mx-0 mt-4 h-8 w-full'
            isDisabled={isLoading || !phone.trim()}
          >
            {isLoading ? 'Sending...' : 'Send Code'}
          </Button>
          <Separator text='or' className='mt-4' />
          <div className='text-center'>
            <button
              type='button'
              onClick={handleBackToSignIn}
              className='text-gray-600 hover:text-gray-800'
            >
              Back to Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PhoneVerificationPage
